import { Close } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slide,
  TextField,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { apiFetch } from '../../lib/api';
import { setCredentials } from '../../store/slices/authSlice';
import { getDashboardUrlForRole } from '../../utils/dashboardRouting';

interface SlideDownRegisterProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onSwitchToLogin?: () => void;
  catalogSlug?: string;
}

interface AddressData {
  street: string;
  city: string;
  state: string;
  zip_code: string;
}

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  companyName: string;
  address: AddressData;
}

export function SlideDownRegister({
  open,
  onClose,
  onSuccess,
  onSwitchToLogin,
  catalogSlug,
}: SlideDownRegisterProps) {
  const router = useRouter();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip_code: '',
    },
  });

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof RegisterFormData] as any),
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const extractToken = (result: any) =>
    result?.tokens?.accessToken ??
    result?.token ??
    result?.accessToken ??
    result?.jwt ??
    result?.data?.token ??
    null;

  const completeRegistration = async (result: any) => {
    console.log('[SlideDownRegister] ===== REGISTRATION COMPLETION START =====');
    console.log('[SlideDownRegister] Registration result received:', {
      hasToken: !!result?.token,
      hasUser: !!result?.user,
      userRole: result?.user?.role,
      userId: result?.user?.id,
    });

    const token = extractToken(result);
    console.log('[SlideDownRegister] Token extracted:', {
      tokenPresent: !!token,
      tokenLength: token?.length,
      tokenPrefix: token ? `${token.substring(0, 20)}...` : 'null',
    });

    if (!token) {
      throw new Error('Registration failed: No token received');
    }

    console.log('[SlideDownRegister] User data from response:', {
      role: result.user?.role,
      email: result.user?.email,
      id: result.user?.id,
      tenantId: result.user?.tenantId,
    });

    console.log('[SlideDownRegister] Dispatching setCredentials to Redux');
    // Transform API response to match setCredentials expected structure
    const credentialsPayload = {
      token: token, // Use the extracted token
      user: result.user,
    };
    dispatch(setCredentials(credentialsPayload));

    console.log('[SlideDownRegister] Token persisted via setCredentials');

    console.log('[SlideDownRegister] Checking Redux state after dispatch');
    setTimeout(() => {
      const currentState = (window as any).__REDUX_STORE__?.getState?.()?.auth;
      console.log('[SlideDownRegister] Redux state after dispatch:', {
        tokenPresent: !!currentState?.token,
        userPresent: !!currentState?.user,
        userRole: currentState?.user?.role,
        userEmail: currentState?.user?.email,
      });
    }, 100);

    console.log('[SlideDownRegister] Navigating based on user role...');
    const userRole = result.user?.role;
    let redirectPath = getDashboardUrlForRole(userRole);

    // For catalog-specific registration, redirect to catalog
    if (catalogSlug) {
      redirectPath = `/c/${catalogSlug}`;
    }

    console.log(
      '[SlideDownRegister] User role:',
      userRole,
      'catalogSlug:',
      catalogSlug,
      'redirecting to:',
      redirectPath,
    );

    // Ensure Redux state is updated before navigation
    await new Promise((resolve) => setTimeout(resolve, 50));

    // Use role-based redirect path
    await router.push(redirectPath);
    console.log('[SlideDownRegister] ===== REGISTRATION COMPLETION END =====');
  };

  const handleCreateAccount = async () => {
    // Validate required fields
    if (
      !formData.email ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.address.street ||
      !formData.address.city ||
      !formData.address.state ||
      !formData.address.zip_code
    ) {
      setError('All fields are required');
      return;
    }

    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create the account with address using customer registration endpoint
      const registerData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        company: formData.companyName || null,
        address: formData.address,
        return_catalog: catalogSlug,
      };

      console.log('[SlideDownRegister] Registration payload:', {
        email: registerData.email,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
        hasAddress: !!registerData.address,
        returnCatalog: registerData.return_catalog,
      });

      const response = await apiFetch('/auth/register/customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData),
      });

      console.log('[SlideDownRegister] Registration result structure:', {
        hasToken: !!extractToken(response),
        hasUser: !!response?.user,
        resultKeys: Object.keys(response || {}),
      });

      console.log('[SlideDownRegister] Final result before completeRegistration:', {
        hasToken: !!extractToken(response),
        hasUser: !!response?.user,
        userRole: response?.user?.role,
        userEmail: response?.user?.email,
      });

      await completeRegistration(response);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      console.log('[SlideDownRegister] Unexpected error in handleCreateAccount:', err);
      const status =
        err?.status ?? err?.originalStatus ?? err?.data?.status ?? err?.response?.status;
      const apiMsg =
        err?.data?.message ??
        err?.response?.data?.message ??
        (typeof err?.error === 'string' ? err.error : null);

      let msg = 'Registration failed';
      if (status === 400 && /email.*already.*exists/i.test(String(apiMsg || '')))
        msg = 'Registration failed: Email already exists';
      else if (status === 422) msg = 'Registration failed: Invalid data provided';
      else if (apiMsg) msg = `Registration failed: ${apiMsg}`;

      console.log('[SlideDownRegister] Setting final error:', msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      companyName: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip_code: '',
      },
    });
    setError(null);
  };

  React.useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  return (
    <Slide direction="down" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          top: 64, // Below header
          left: 0,
          right: 0,
          zIndex: 1300,
          maxHeight: 'calc(100vh - 64px)',
          overflow: 'auto',
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header with close button */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
              {catalogSlug ? `Join ${catalogSlug} Marketplace` : 'Join TechAdds Marketplace'}
            </Typography>
            <IconButton onClick={onClose} disabled={loading}>
              <Close />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create your account to start browsing and ordering services. Your address will be used
            as your default service location.
          </Typography>

          <Typography
            variant="body2"
            color="primary"
            sx={{ mb: 3, cursor: 'pointer' }}
            onClick={onSwitchToLogin}
          >
            Already have an account? Login now →
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Registration Form */}
          <Grid container spacing={3}>
            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                required
                disabled={loading}
                helperText="Must be at least 8 characters"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                required
                disabled={loading}
                helperText="Re-enter your password"
              />
            </Grid>

            {/* Service Address */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, mt: 2 }}>
                Service Address
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This will be your default address for service delivery and billing.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address.street}
                onChange={(e) => handleInputChange('address.street', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.address.city}
                onChange={(e) => handleInputChange('address.city', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required disabled={loading}>
                <InputLabel>State</InputLabel>
                <Select
                  value={formData.address.state}
                  onChange={(e) => handleInputChange('address.state', e.target.value)}
                  label="State"
                >
                  <MenuItem value="AL">Alabama</MenuItem>
                  <MenuItem value="AK">Alaska</MenuItem>
                  <MenuItem value="AZ">Arizona</MenuItem>
                  <MenuItem value="AR">Arkansas</MenuItem>
                  <MenuItem value="CA">California</MenuItem>
                  <MenuItem value="CO">Colorado</MenuItem>
                  <MenuItem value="CT">Connecticut</MenuItem>
                  <MenuItem value="DE">Delaware</MenuItem>
                  <MenuItem value="FL">Florida</MenuItem>
                  <MenuItem value="GA">Georgia</MenuItem>
                  <MenuItem value="HI">Hawaii</MenuItem>
                  <MenuItem value="ID">Idaho</MenuItem>
                  <MenuItem value="IL">Illinois</MenuItem>
                  <MenuItem value="IN">Indiana</MenuItem>
                  <MenuItem value="IA">Iowa</MenuItem>
                  <MenuItem value="KS">Kansas</MenuItem>
                  <MenuItem value="KY">Kentucky</MenuItem>
                  <MenuItem value="LA">Louisiana</MenuItem>
                  <MenuItem value="ME">Maine</MenuItem>
                  <MenuItem value="MD">Maryland</MenuItem>
                  <MenuItem value="MA">Massachusetts</MenuItem>
                  <MenuItem value="MI">Michigan</MenuItem>
                  <MenuItem value="MN">Minnesota</MenuItem>
                  <MenuItem value="MS">Mississippi</MenuItem>
                  <MenuItem value="MO">Missouri</MenuItem>
                  <MenuItem value="MT">Montana</MenuItem>
                  <MenuItem value="NE">Nebraska</MenuItem>
                  <MenuItem value="NV">Nevada</MenuItem>
                  <MenuItem value="NH">New Hampshire</MenuItem>
                  <MenuItem value="NJ">New Jersey</MenuItem>
                  <MenuItem value="NM">New Mexico</MenuItem>
                  <MenuItem value="NY">New York</MenuItem>
                  <MenuItem value="NC">North Carolina</MenuItem>
                  <MenuItem value="ND">North Dakota</MenuItem>
                  <MenuItem value="OH">Ohio</MenuItem>
                  <MenuItem value="OK">Oklahoma</MenuItem>
                  <MenuItem value="OR">Oregon</MenuItem>
                  <MenuItem value="PA">Pennsylvania</MenuItem>
                  <MenuItem value="RI">Rhode Island</MenuItem>
                  <MenuItem value="SC">South Carolina</MenuItem>
                  <MenuItem value="SD">South Dakota</MenuItem>
                  <MenuItem value="TN">Tennessee</MenuItem>
                  <MenuItem value="TX">Texas</MenuItem>
                  <MenuItem value="UT">Utah</MenuItem>
                  <MenuItem value="VT">Vermont</MenuItem>
                  <MenuItem value="VA">Virginia</MenuItem>
                  <MenuItem value="WA">Washington</MenuItem>
                  <MenuItem value="WV">West Virginia</MenuItem>
                  <MenuItem value="WI">Wisconsin</MenuItem>
                  <MenuItem value="WY">Wyoming</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.address.zip_code}
                onChange={(e) => handleInputChange('address.zip_code', e.target.value)}
                required
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleCreateAccount}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} /> : null}
                sx={{ py: 1.5 }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Slide>
  );
}

export default SlideDownRegister;
